import { Layout } from "@/components/layout/Layout";
import { usePageMeta } from "@/hooks/use-page-meta";
import { projects } from "@/data/projects";
import { useState } from "react";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Work() {
  usePageMeta("Work", "Portfolio of digital products, ecommerce systems, healthcare platforms, and complex projects built by Travis Johnson.");
  
  const [activeTab, setActiveTab] = useState("All");
  
  const categories = ["All", "Technology", "Ecommerce", "Healthcare", "Real Estate", "Media", "Payments", "Business Development", "Nonprofit", "Experiments"];
  
  const filteredProjects = activeTab === "All" 
    ? projects 
    : projects.filter(p => p.categories.includes(activeTab));

  return (
    <Layout>
      <div className="container mx-auto px-6 max-w-6xl">
        <header className="py-16 md:py-24 border-b border-border mb-12">
          <h1 className="text-4xl md:text-6xl font-serif font-medium tracking-tight mb-6">Selected Work</h1>
          <p className="text-xl text-muted-foreground max-w-3xl leading-relaxed">
            Case studies exploring the systems, architecture, and operational challenges behind complex digital projects.
          </p>
          <div className="mt-8 p-4 bg-secondary/50 rounded-lg border border-border/50 inline-block">
             <p className="text-sm text-muted-foreground italic border-l-2 border-accent pl-3">
              Much of my work happens behind the scenes. These examples focus on the problems, responsibilities, and systems involved rather than exposing private business information.
            </p>
          </div>
        </header>

        <div className="mb-12 overflow-x-auto pb-4 -mx-6 px-6 md:overflow-visible md:pb-0 md:mx-0 md:px-0 scrollbar-hide">
          <Tabs defaultValue="All" className="w-full" onValueChange={setActiveTab}>
            <TabsList className="h-auto p-1 bg-secondary/50 border border-border inline-flex flex-wrap md:flex-nowrap gap-1">
              {categories.map(cat => (
                <TabsTrigger 
                  key={cat} 
                  value={cat}
                  className="rounded-md data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm px-4 py-2 text-sm font-medium transition-all"
                >
                  {cat}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="grid grid-cols-1 gap-12 lg:gap-16">
          {filteredProjects.map(project => (
            <Link key={project.slug} href={`/work/${project.slug}`}>
              <div className="group grid grid-cols-1 md:grid-cols-12 gap-8 items-center bg-card rounded-3xl p-6 md:p-10 border border-border hover:border-accent/30 hover:shadow-md transition-all cursor-pointer">
                
                <div className="md:col-span-7 lg:col-span-7 space-y-6 order-2 md:order-1">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-medium">
                      {project.industry}
                    </Badge>
                    {project.confidential && (
                      <Badge variant="outline" className="border-border text-muted-foreground">
                        Confidential
                      </Badge>
                    )}
                  </div>
                  
                  <div>
                    <h2 className="text-2xl md:text-3xl font-serif font-medium mb-2 group-hover:text-accent transition-colors">
                      {project.name}
                    </h2>
                    <p className="text-foreground/80 font-medium">{project.role}</p>
                  </div>
                  
                  <p className="text-muted-foreground leading-relaxed text-lg">
                    {project.summary}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 pt-4">
                    {project.areas.map(area => (
                      <span key={area} className="text-xs font-medium px-2.5 py-1 rounded-md bg-secondary/50 text-muted-foreground border border-border/50">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className={`md:col-span-5 lg:col-span-5 aspect-video md:aspect-square lg:aspect-[4/3] w-full rounded-xl border border-border/50 order-1 md:order-2 flex items-center justify-center overflow-hidden ${project.imageStyle === 'cover' || project.imageStyle === 'contain-dark' ? 'bg-zinc-900' : 'bg-white dark:bg-zinc-800'}`}>
                  {project.image ? (
                    <img
                      src={project.image}
                      alt={project.imageAlt}
                      className={`w-full h-full ${project.imageStyle === 'cover' ? 'object-cover' : 'object-contain p-8'}`}
                    />
                  ) : (
                    <span className="text-muted-foreground/30 font-serif italic text-sm px-6 text-center">
                      [{project.imageAlt}]
                    </span>
                  )}
                </div>

              </div>
            </Link>
          ))}
          
          {filteredProjects.length === 0 && (
            <div className="py-24 text-center text-muted-foreground">
              No projects found in this category.
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
